import React, { RefObject, useEffect, useRef, useState } from "react";

import getPaidFeatures, { LicenseKeyFeaturesList } from "../../../../api/st/features/get";
import CheckBox from "../../../elements/checkbox";
import InlineNotification from "../../../elements/Notification";
import { getLicenseKeySelectedFeatures } from "../../../../api/saas/multi-tenancy/connection-uri-domain/appid/license/get";
import { updateOrCreateLicenseKey } from "../../../../api/saas/multi-tenancy/connection-uri-domain/appid/license/put";
import { Dropdown } from "../../../dropdown";
import { AppsListWithOnlyDeploymentsData } from "../EnablePaidFeaturesModal";
import saasAnalytics from "../../../../dashboard/saas/saasAnalytics";
import compareSemanticVersions from "../../../../utils/compareSemanticVersions";
import { clientSideOnlyFeatureList, featureIDToPricingConfig } from "./config";

type GenerateLicenseKeyForManagedServiceProps = {
    deploymentId: string;
    onCloseModal: () => void;
    initiallySelectedApp: string;
    appsList: AppsListWithOnlyDeploymentsData;
};

//this function will check weather a particular core version is compatible with a minimum required core version for a feature.
function checkForCoreCompatiblilityWithFeature(
    appList: AppsListWithOnlyDeploymentsData,
    selectedApp: string,
    minimumRequiredCoreVersion: string
) {
    let selectedAppDetails = appList.find(app => app.appName === selectedApp);

    if (selectedAppDetails === undefined) {
        selectedAppDetails = appList[0];
        // throw new Error("This should never happen, selected app should always be there")
    }

    const prodCoreVersion =
        selectedAppDetails.prodDeployment !== undefined &&
        selectedAppDetails.prodDeployment.displayCoreVersion !== undefined
            ? selectedAppDetails.prodDeployment.displayCoreVersion
            : minimumRequiredCoreVersion;
    const devCoreVersion =
        selectedAppDetails.devDeployment !== undefined &&
        selectedAppDetails.devDeployment.displayCoreVersion !== undefined
            ? selectedAppDetails.devDeployment.displayCoreVersion
            : minimumRequiredCoreVersion;

    const isProdCoreCompatible = compareSemanticVersions(prodCoreVersion, minimumRequiredCoreVersion) >= 0;
    const isDevCoreComaptible = compareSemanticVersions(devCoreVersion, minimumRequiredCoreVersion) >= 0;

    return {
        isDevCoreComaptible,
        isProdCoreCompatible
    };
}

//get the selected features that needs to be upgraded by the user for a app.
function getUpgradeNeedFeatureList(
    featuresList: LicenseKeyFeaturesList,
    appList: AppsListWithOnlyDeploymentsData,
    selectedApp: string,
    selectedFeatures: string[]
) {
    const upgradeNeedFeatures = [];

    for (let i = 0; i < featuresList.length; i++) {
        const feature = featuresList[i];
        const { isDevCoreComaptible, isProdCoreCompatible } = checkForCoreCompatiblilityWithFeature(
            appList,
            selectedApp,
            feature.minVersionXY
        );

        //add a particular feature to upgrade needed feature list only when they are explicitly selected by
        //the user even they have incompatible versions with the core... or else ignore that adding to the list.
        if (selectedFeatures.includes(feature.featureId) === true) {
            if (isDevCoreComaptible === false || isProdCoreCompatible === false) {
                upgradeNeedFeatures.push(feature.featureId);
            }
        }
    }

    return upgradeNeedFeatures;
}

export default function GenerateLicenseKeyForManagedService({
    onCloseModal,
    deploymentId,
    appsList,
    initiallySelectedApp
}: GenerateLicenseKeyForManagedServiceProps) {
    //used to store selected features lists by the user.
    const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
    //use to track the selected app form the dropdown if there are multiple apps
    //or else by default it have "public" as the selectedApp.
    const [selectedApp, setSelectedApp] = useState<string>(initiallySelectedApp);

    //features are list availble paid features with there featureId's and minimum required core versions.
    const [features, setFeatures] = useState<LicenseKeyFeaturesList>([]);
    //state used to managed the http request getFeatures request state.
    const [fetchingFeaturesStatus, setFetchingFeaturesStatus] = useState<"loading" | "error" | "success">("loading");

    //used to managed update license key http request state.
    const [updateLicenseKeyStatus, setUpdateLicenseKeyStatus] = useState<
        "loading" | "error" | "success" | "not-clicked"
    >("not-clicked");

    const upgradeNeededFeatures = getUpgradeNeedFeatureList(features, appsList, selectedApp, selectedFeatures);

    const refs = useRef<RefObject<HTMLDivElement>[]>([]);
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

    const setRef = (el: HTMLDivElement | null, index: number) => {
        if (el) {
            refs.current[index] = { current: el };
        }
    };

    function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (e.currentTarget.checked) {
            saasAnalytics.enablePaidFeaturesSelecteFeatureClick("managed", e.currentTarget.id);
            setSelectedFeatures([...selectedFeatures, e.currentTarget.id]);
        } else {
            setSelectedFeatures(selectedFeatures.filter(featureId => featureId !== e.currentTarget.id));
        }
    }

    async function getFeatures() {
        try {
            setFetchingFeaturesStatus("loading");
            const paidFeaturesList = await getPaidFeatures();
            const selectedFeatureList = await getLicenseKeySelectedFeatures(selectedApp, {
                deploymentId
            });

            //to put multitenacy to be the first feature to render in the selected features list
            //no matter what order the api returns the results.
            const multitenancy = paidFeaturesList.filter(feature => feature.featureId === "multi_tenancy");
            const restFeatures = paidFeaturesList.filter(feature => feature.featureId !== "multi_tenancy");

            setFeatures([...multitenancy, ...restFeatures]);

            //adding `multi_tenancy` as a default feature to the selected feature list.
            const finalSelectedFeatures =
                selectedFeatureList.featureIds.includes("multi_tenancy") === true
                    ? selectedFeatureList.featureIds
                    : ["multi_tenancy", ...selectedFeatureList.featureIds];
            setSelectedFeatures(finalSelectedFeatures);
            setFetchingFeaturesStatus("success");
        } catch (_) {
            setFetchingFeaturesStatus("error");
        }
    }

    function renderNote(featureId: string) {
        const currentFeature = features.find(f => f.featureId === featureId);

        if (currentFeature !== undefined) {
            const { isDevCoreComaptible, isProdCoreCompatible } = checkForCoreCompatiblilityWithFeature(
                appsList,
                selectedApp,
                currentFeature.minVersionXY
            );

            if (isDevCoreComaptible === false && isProdCoreCompatible === false) {
                return (
                    <p className="upgrade-note">
                        Upgrade your production and developement core version to v{currentFeature.minVersionXY} to use
                        this feature.
                    </p>
                );
            }

            if (isDevCoreComaptible === false) {
                return (
                    <p className="upgrade-note">
                        Upgrade your development core version to v{currentFeature.minVersionXY} to use this feature.
                    </p>
                );
            }

            if (isProdCoreCompatible === false) {
                return (
                    <p className="upgrade-note">
                        Upgrade your production core version to v{currentFeature.minVersionXY} to use this feature.
                    </p>
                );
            }
        }

        return null;
    }

    async function updateLicenseKey() {
        if (selectedFeatures.length === 0) {
            return;
        }

        setUpdateLicenseKeyStatus("loading");

        try {
            saasAnalytics.enablePaidFeaturesGenerateLicenseKeyForSaas(selectedFeatures, selectedApp);
            await updateOrCreateLicenseKey(selectedApp, {
                deploymentId,
                featureIds: selectedFeatures
            });
            setUpdateLicenseKeyStatus("success");
        } catch (_) {
            setUpdateLicenseKeyStatus("error");
        }
    }

    useEffect(() => {
        getFeatures();
    }, [selectedApp]);

    if (updateLicenseKeyStatus === "success") {
        return (
            <div className="modal-container max-content generate-license-key-modal">
                <div className="modal-header generate-license-key-modal-header">
                    <h2>Done!</h2>
                    <span
                        onClick={() => {
                            saasAnalytics.enablePaidFeaturesModalClose("success-managed");
                            onCloseModal();
                        }}
                    >
                        <img src="static/assets/dashboard/close-rounded.png" />
                    </span>
                </div>
                <div className="modal-body">
                    <div className="success-container">
                        <div className="message-container">
                            <img src="/static/assets/dashboard/green-check.svg" alt="check" />
                            <div className="managed-service-success">
                                <p>
                                    Features mentioned below are now enabled for your app ID:{" "}
                                    <span className="bold-text">{selectedApp}</span>
                                </p>
                                <hr />
                                <ul>
                                    {selectedFeatures.map(feature => {
                                        return (
                                            <li key={feature}>
                                                {[...features, ...clientSideOnlyFeatureList].find(f => f.featureId === feature)?.featureName} {renderNote(feature)}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`modal-container generate-license-key-modal ${
                activeTooltip ? "modal-with-popover" : ""
            }`}
        >
            <div className="modal-header generate-license-key-modal-header">
                <h2>Enable/Disable Paid Features</h2>
                <span
                    onClick={() => {
                        saasAnalytics.enablePaidFeaturesModalClose("generate-licensekey-for-managed-service");
                        onCloseModal();
                    }}
                >
                    <img src="static/assets/dashboard/close-rounded.png" />
                </span>
            </div>
            <div className="modal-body">
                <div className="select-feature-heading-note">
                    <span>NOTE</span>
                    <p>
                        Enabling any of the features below will activate them across all environments{" "}
                        <strong>(both development and production environments)</strong>.
                    </p>
                    <p>
                        If the production environment has not been deployed you wont be charged. Charges
                        will apply as soon as a production environment is deployed and there is at least one Monthly
                        Active User (MAU).
                    </p>
                </div>
                {appsList.length > 1 ? (
                    <>
                        <div className="select-feature-container">
                            <span className="select-feature-heading">Select App</span>
                        </div>
                        <div
                            className={`select-feature-body select-app-body ${
                                updateLicenseKeyStatus === "loading" || fetchingFeaturesStatus === "loading"
                                    ? "pointer-events-none"
                                    : ""
                            }`}
                        >
                            <Dropdown<string>
                                defaultText="Select App"
                                selectedValue={selectedApp}
                                items={appsList.map(({ appName }) => {
                                    return {
                                        value: appName,
                                        text: appName
                                    };
                                })}
                                onChange={value => {
                                    setSelectedApp(value);
                                }}
                            />
                        </div>
                    </>
                ) : null}
                <div className="select-feature-container">
                    <span className="select-feature-heading">Select required features</span>
                    <span className="select-price-heading">Price</span>
                </div>
                <div className="select-feature-body">
                    {fetchingFeaturesStatus === "loading" ? (
                        <div className="center">
                            <img className="loader" src="/static/assets/dashboard/loading.svg" alt="loading" />
                        </div>
                    ) : null}
                    {fetchingFeaturesStatus === "error" ? (
                        <div className="center">
                            <div className="error-msg-container">
                                <img height={16} width={18} src="/static/assets/dashboard/alert.png" alt="alert" />
                                <p className="error-msg">Something went wrong. Please try again!</p>
                            </div>
                        </div>
                    ) : null}
                    {fetchingFeaturesStatus === "success" ? (
                        <div
                            className={`checkboxes-container ${
                                updateLicenseKeyStatus === "loading" ? "pointer-events-none" : ""
                            }`}
                        >
                            {[...features, ...clientSideOnlyFeatureList].map((feature, index) => {
                                const isChecked =
                                    selectedFeatures.includes(feature.featureId) ||
                                    feature.featureId === "multi_tenancy";
                                const featureConfig =
                                    featureIDToPricingConfig[
                                        feature.featureId as keyof typeof featureIDToPricingConfig
                                    ];
                                return (
                                    <div key={feature.featureId} className="feature-item">
                                        <div className="checkbox-container">
                                            <CheckBox
                                                key={feature.featureId}
                                                className="size-16"
                                                id={feature.featureId}
                                                onChange={handleCheckboxChange}
                                                color="primary"
                                                disabled={
                                                    feature.featureId === "multi_tenancy" ||
                                                    clientSideOnlyFeatureList.includes(feature)
                                                }
                                                checked={isChecked}
                                            />
                                            <label
                                                data-disabled={feature.featureId === "multi_tenancy" ? "true" : "false"}
                                                htmlFor={feature.featureId}
                                            >
                                                {feature.featureId === "multi_tenancy"
                                                    ? "Multitenancy (Enabled by default)"
                                                    : feature.featureName}
                                            </label>
                                        </div>
                                        {isChecked === true ? (
                                            <UpgradeWarning
                                                appList={appsList}
                                                selectedApp={selectedApp}
                                                minCoreVersion={feature.minVersionXY}
                                                upgradeNeededFeatures={upgradeNeededFeatures}
                                            />
                                        ) : null}
                                        <div className="feature-price-detail">
                                            <div className="feature-price-container">
                                                {(() => {
                                                    switch (featureConfig.type) {
                                                        case "ATTACK_PROTECTION_SUITE":
                                                        case "UNIFIED_LOGIN_AND_M2M":
                                                            return (
                                                                <a
                                                                    className="feature-price"
                                                                    href="/consultancy"
                                                                    target="_blank"
                                                                >
                                                                    {featureConfig.pricing.text}
                                                                </a>
                                                            );
                                                        default:
                                                            return (
                                                                <span className="feature-price">
                                                                    {featureConfig.pricing.text}
                                                                </span>
                                                            );
                                                    }
                                                })()}

                                                {featureConfig.hasDialog ? (
                                                    <div
                                                        className="info-icon-container"
                                                        ref={el => setRef(el, index)}
                                                        onMouseEnter={() => setActiveTooltip(feature.featureId)}
                                                        onMouseLeave={() => setActiveTooltip(null)}
                                                    >
                                                        <svg
                                                            width="12"
                                                            height="13"
                                                            viewBox="0 0 12 13"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M5.6875 0.8125C4.56262 0.8125 3.463 1.14607 2.5277 1.77102C1.59239 2.39597 0.863411 3.28423 0.432938 4.32349C0.00246429 5.36274 -0.110167 6.50631 0.109286 7.60958C0.32874 8.71284 0.870422 9.72626 1.66583 10.5217C2.46124 11.3171 3.47466 11.8588 4.57793 12.0782C5.68119 12.2977 6.82476 12.185 7.86401 11.7546C8.90327 11.3241 9.79153 10.5951 10.4165 9.6598C11.0414 8.7245 11.375 7.62488 11.375 6.5C11.3734 4.99207 10.7737 3.54636 9.70741 2.48009C8.64114 1.41382 7.19543 0.814092 5.6875 0.8125ZM5.6875 11.3125C4.73568 11.3125 3.80523 11.0303 3.01382 10.5014C2.22241 9.97264 1.60558 9.22103 1.24133 8.34166C0.877085 7.46229 0.781782 6.49466 0.967473 5.56113C1.15316 4.62759 1.61151 3.77009 2.28455 3.09705C2.95759 2.42401 3.8151 1.96566 4.74863 1.77997C5.68216 1.59428 6.6498 1.68958 7.52917 2.05383C8.40854 2.41808 9.16015 3.03491 9.68895 3.82632C10.2178 4.61773 10.5 5.54818 10.5 6.5C10.4986 7.77591 9.99106 8.99915 9.08885 9.90135C8.18665 10.8036 6.96341 11.3111 5.6875 11.3125ZM6.5625 9.125C6.5625 9.24103 6.51641 9.35231 6.43436 9.43436C6.35231 9.51641 6.24103 9.5625 6.125 9.5625C5.89294 9.5625 5.67038 9.47031 5.50628 9.30622C5.34219 9.14212 5.25 8.91956 5.25 8.6875V6.5C5.13397 6.5 5.02269 6.45391 4.94064 6.37186C4.8586 6.28981 4.8125 6.17853 4.8125 6.0625C4.8125 5.94647 4.8586 5.83519 4.94064 5.75314C5.02269 5.67109 5.13397 5.625 5.25 5.625C5.48207 5.625 5.70463 5.71719 5.86872 5.88128C6.03281 6.04538 6.125 6.26794 6.125 6.5V8.6875C6.24103 8.6875 6.35231 8.73359 6.43436 8.81564C6.51641 8.89769 6.5625 9.00897 6.5625 9.125ZM4.8125 4.09375C4.8125 3.96396 4.85099 3.83708 4.9231 3.72916C4.99521 3.62124 5.0977 3.53712 5.21762 3.48745C5.33753 3.43778 5.46948 3.42479 5.59678 3.45011C5.72408 3.47543 5.84101 3.53793 5.93279 3.62971C6.02457 3.72149 6.08707 3.83842 6.11239 3.96572C6.13771 4.09302 6.12472 4.22497 6.07505 4.34489C6.02538 4.4648 5.94126 4.56729 5.83334 4.6394C5.72543 4.71151 5.59855 4.75 5.46875 4.75C5.2947 4.75 5.12778 4.68086 5.00471 4.55779C4.88164 4.43472 4.8125 4.2678 4.8125 4.09375Z"
                                                                fill="#007AFF"
                                                            />
                                                        </svg>
                                                        {activeTooltip === feature.featureId &&
                                                            featureConfig.hasDialog && (
                                                                <div className="feature-price-tooltip">
                                                                    {(() => {
                                                                        switch (featureConfig.type) {
                                                                            case "ACCOUNT_LINKING":
                                                                                const AccountLinkingDialog =
                                                                                    featureConfig.dialog;
                                                                                const AccountLinkingConfig =
                                                                                    featureConfig.dialogConfig;
                                                                                return (
                                                                                    <AccountLinkingDialog
                                                                                        config={AccountLinkingConfig}
                                                                                    />
                                                                                );
                                                                            case "DASHBOARD_LOGIN":
                                                                                const DashboardLoginDialog =
                                                                                    featureConfig.dialog;
                                                                                const DashboardLoginConfig =
                                                                                    featureConfig.dialogConfig;
                                                                                return (
                                                                                    <DashboardLoginDialog
                                                                                        config={DashboardLoginConfig}
                                                                                    />
                                                                                );

                                                                            case "MULTI_TENANCY":
                                                                                const MultiTenancyDialog =
                                                                                    featureConfig.dialog;
                                                                                const MultiTenancyConfig =
                                                                                    featureConfig.dialogConfig;
                                                                                return (
                                                                                    <MultiTenancyDialog
                                                                                        config={MultiTenancyConfig}
                                                                                    />
                                                                                );
                                                                        }
                                                                    })()}
                                                                </div>
                                                            )}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className="feature-sub-text">
                                                {"minimumBilling" in featureConfig.pricing ? (
                                                    <span>
                                                        Minimum Billing of $
                                                        <span className="feature-minimum-billing">
                                                            {featureConfig.pricing.minimumBilling} /month
                                                        </span>
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
                {fetchingFeaturesStatus !== "loading" && upgradeNeededFeatures.length > 0 ? (
                    <div className="margin-top-8">
                        <div className="warning-container">
                            {upgradeNeededFeatures.length > 1 ? (
                                <p className="bold margin-bottom-2">
                                    Please{" "}
                                    <a
                                        className="anchor"
                                        href="mailto:support@supertokens.com?subject=Request to upgrade the core."
                                    >
                                        Contact us
                                    </a>{" "}
                                    to upgrade the core.
                                </p>
                            ) : null}
                            <p>
                                <span className="bold">Note</span>: If you click on save, the feature will be auto
                                enabled when the core is upgraded.
                            </p>
                        </div>
                    </div>
                ) : null}
                {updateLicenseKeyStatus === "error" ? (
                    <div className="notification-holder margin-bottom-28 width-fit-content">
                        <InlineNotification status="error">Something went wrong. Please try again!</InlineNotification>
                    </div>
                ) : null}
            </div>
            <div className="modal-action-container margin-top-20">
                {fetchingFeaturesStatus === "error" ? (
                    <button onClick={getFeatures} className="btn btn-sm white-button">
                        Retry
                    </button>
                ) : null}
                <button
                    onClick={updateLicenseKey}
                    disabled={updateLicenseKeyStatus === "loading"}
                    className={`"yellow-button-loading btn btn-sm yellow-button`}
                >
                    Save
                    {updateLicenseKeyStatus === "loading" ? (
                        <img
                            height={20}
                            className="loader"
                            src="/static/assets/dashboard/white-loader.svg"
                            alt="loading"
                        />
                    ) : null}
                </button>
            </div>
        </div>
    );
}

type UpgradeWarningProps = {
    appList: AppsListWithOnlyDeploymentsData;
    selectedApp: string;
    minCoreVersion: string;
    upgradeNeededFeatures: string[];
};

function UpgradeWarning({ minCoreVersion, appList, selectedApp, upgradeNeededFeatures }: UpgradeWarningProps) {
    let contactUs: JSX.Element | null = null;

    if (upgradeNeededFeatures.length <= 1) {
        contactUs = (
            <span>
                <a
                    className="underline"
                    href="mailto:support@supertokens.com?subject=Request to upgrade the core version."
                >
                    Contact us
                </a>{" "}
                to upgrade the core.
            </span>
        );
    }

    const { isDevCoreComaptible, isProdCoreCompatible } = checkForCoreCompatiblilityWithFeature(
        appList,
        selectedApp,
        minCoreVersion
    );

    if (isDevCoreComaptible === false && isProdCoreCompatible === false) {
        return (
            <InlineNotification size="sm" status="error">
                This feature requires minimum core version of v{minCoreVersion} on your development and production
                environments. {contactUs}
            </InlineNotification>
        );
    }

    if (isDevCoreComaptible === false) {
        return (
            <InlineNotification size="sm" status="error">
                This feature requires minimum core version of v{minCoreVersion} on your development environment.{" "}
                {contactUs}
            </InlineNotification>
        );
    }

    if (isProdCoreCompatible === false) {
        return (
            <InlineNotification size="sm" status="error">
                This feature requires minimum core version of v{minCoreVersion} on your production environment.{" "}
                {contactUs}
            </InlineNotification>
        );
    }

    return null;
}
